import { useEffect, useRef, useState } from "react"
import {
	DEFAULT_ZOOM,
	DRAG_SCROLL_EDGE_PX,
	DRAG_SCROLL_MAX_SPEED,
	MAX_TIMELINE_HEIGHT,
	MAX_ZOOM,
	MIN_TIMELINE_HEIGHT,
	MIN_ZOOM,
	ROW_HEIGHT,
	SIDEBAR_WIDTH,
	TICKS_RULER_HEIGHT,
	TIMELINE_CHROME_HEIGHT,
	WHEEL_ZOOM_FACTOR,
} from "./utils"

const FALLBACK_TRACK_WIDTH = 800

// Panel height (auto-sized to route count) and horizontal zoom (buttons +
// Ctrl/Cmd+scroll) — bundled since both just size the panel for the caller.
export function useTimelineLayout(routeCount: number) {
	const height = Math.min(
		Math.max(
			TIMELINE_CHROME_HEIGHT + TICKS_RULER_HEIGHT + routeCount * ROW_HEIGHT,
			MIN_TIMELINE_HEIGHT
		),
		MAX_TIMELINE_HEIGHT
	)

	// Horizontal zoom: 1x fits the panel's own (measured) width; zooming in
	// spreads stops out for finer control, with horizontal scroll picking up
	// the rest.
	const [zoom, setZoom] = useState(DEFAULT_ZOOM)
	const scrollRef = useRef<HTMLDivElement>(null)
	// Measured from the header row, not the scroller itself — the scroller's
	// clientWidth shrinks when its own scrollbar appears, which would trigger
	// a resize feedback loop (shrink → scrollbar off → grow → repeat).
	const measureRef = useRef<HTMLDivElement>(null)
	const [baseTrackWidth, setBaseTrackWidth] = useState(FALLBACK_TRACK_WIDTH)

	useEffect(() => {
		const el = measureRef.current
		if (!el) return
		const measure = () =>
			setBaseTrackWidth(Math.max(el.clientWidth - SIDEBAR_WIDTH, 400))
		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(el)
		return () => observer.disconnect()
	}, [])

	// Multiplicative — see ZOOM_BUTTON_FACTOR for why.
	const zoomBy = (factor: number) =>
		setZoom((prev) => Math.min(Math.max(prev * factor, MIN_ZOOM), MAX_ZOOM))
	// "Fit all routes" — MIN_ZOOM is the panel's own measured width.
	const zoomToFit = () => setZoom(MIN_ZOOM)

	// Ctrl/Cmd+scroll zooms the time axis. Native listener, not React's
	// `onWheel` — that's passive by default, so preventDefault() is ignored
	// and the browser zooms the whole page instead.
	useEffect(() => {
		const el = scrollRef.current
		if (!el) return
		const handleWheelNative = (e: WheelEvent) => {
			if (!e.ctrlKey && !e.metaKey) return
			e.preventDefault()
			zoomBy(e.deltaY < 0 ? WHEEL_ZOOM_FACTOR : 1 / WHEEL_ZOOM_FACTOR)
		}
		el.addEventListener("wheel", handleWheelNative, { passive: false })
		return () => el.removeEventListener("wheel", handleWheelNative)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Auto-scroll near the container's edge while dragging — native HTML5 drag
	// never scrolls on its own. `dragover` fires continuously while hovering,
	// so it doubles as the animation driver; the other listeners just stop it.
	useEffect(() => {
		const el = scrollRef.current
		if (!el) return

		let frame: number | null = null
		let dx = 0
		let dy = 0

		const tick = () => {
			if (dx !== 0 || dy !== 0) {
				el.scrollLeft += dx
				el.scrollTop += dy
				frame = requestAnimationFrame(tick)
			} else {
				frame = null
			}
		}

		const speedFor = (distanceFromEdge: number) => {
			const clamped = Math.max(distanceFromEdge, 0)
			return clamped >= DRAG_SCROLL_EDGE_PX
				? 0
				: DRAG_SCROLL_MAX_SPEED * (1 - clamped / DRAG_SCROLL_EDGE_PX)
		}

		const handleDragOver = (e: DragEvent) => {
			const rect = el.getBoundingClientRect()
			// Offset by SIDEBAR_WIDTH — it sits on top of the track's left edge.
			const distLeft = e.clientX - rect.left - SIDEBAR_WIDTH
			const distRight = rect.right - e.clientX
			const distTop = e.clientY - rect.top
			const distBottom = rect.bottom - e.clientY

			dx = distLeft < distRight ? -speedFor(distLeft) : speedFor(distRight)
			dy = distTop < distBottom ? -speedFor(distTop) : speedFor(distBottom)

			if ((dx !== 0 || dy !== 0) && frame == null) {
				frame = requestAnimationFrame(tick)
			}
		}

		const stop = () => {
			dx = 0
			dy = 0
		}

		el.addEventListener("dragover", handleDragOver)
		el.addEventListener("dragleave", stop)
		el.addEventListener("drop", stop)
		window.addEventListener("dragend", stop)
		return () => {
			el.removeEventListener("dragover", handleDragOver)
			el.removeEventListener("dragleave", stop)
			el.removeEventListener("drop", stop)
			window.removeEventListener("dragend", stop)
			if (frame != null) cancelAnimationFrame(frame)
		}
	}, [])

	// Click-and-drag panning, like a map. Skips draggable/button targets so it
	// doesn't steal a pill's native drag.
	const [isPanning, setIsPanning] = useState(false)
	useEffect(() => {
		const el = scrollRef.current
		if (!el) return

		let startX = 0
		let startY = 0
		let startScrollLeft = 0
		let startScrollTop = 0

		const handleMouseMove = (e: MouseEvent) => {
			el.scrollLeft = startScrollLeft - (e.clientX - startX)
			el.scrollTop = startScrollTop - (e.clientY - startY)
		}
		const handleMouseUp = () => {
			setIsPanning(false)
			window.removeEventListener("mousemove", handleMouseMove)
			window.removeEventListener("mouseup", handleMouseUp)
		}
		const handleMouseDown = (e: MouseEvent) => {
			const target = e.target as HTMLElement
			if (e.button !== 0 || target.closest('[draggable="true"], button')) return
			e.preventDefault()
			startX = e.clientX
			startY = e.clientY
			startScrollLeft = el.scrollLeft
			startScrollTop = el.scrollTop
			setIsPanning(true)
			window.addEventListener("mousemove", handleMouseMove)
			window.addEventListener("mouseup", handleMouseUp)
		}

		el.addEventListener("mousedown", handleMouseDown)
		return () => {
			el.removeEventListener("mousedown", handleMouseDown)
			window.removeEventListener("mousemove", handleMouseMove)
			window.removeEventListener("mouseup", handleMouseUp)
		}
	}, [])

	return {
		height,
		zoom,
		zoomBy,
		zoomToFit,
		scrollRef,
		measureRef,
		trackWidth: baseTrackWidth * zoom,
		isPanning,
	}
}
